import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useInternalAuth } from '@/hooks/useInternalAuth';
import { formatRoleForDisplay } from '@/utils/roleDisplay';

export const useUserMenuData = () => {
  const { user: supabaseUser, profile: supabaseProfile, signOut: supabaseSignOut } = useAuth();
  const { user: internalUser, profile: internalProfile, logout: internalLogout, refreshUser } = useInternalAuth();
  const [profilePhotoPath, setProfilePhotoPath] = useState<string | null>(null);
  
  // CRITICAL: Prioritize internal user to resolve dual-auth conflicts
  // If internal user exists, use internal user data exclusively
  const user = internalUser || supabaseUser;
  const profile = internalProfile || supabaseProfile;
  const isInternalUser = !!internalUser;
  
  // Force refresh user data on mount to ensure we have latest role from database
  useEffect(() => {
    if (isInternalUser && refreshUser) {
      console.log('🔄 UserMenuData - Refreshing internal user data to get latest role');
      refreshUser();
    }
  }, [isInternalUser, refreshUser]);
  
  // CRITICAL: Role determination - prioritize internal user role (authoritative)
  // This prevents Supabase profile.role from overriding internal user's role
  const roleRaw = useMemo(() => {
    if (internalUser?.role) {
      console.log('🎭 Role from internalUser:', internalUser.role);
      return internalUser.role;
    }
    if (internalProfile?.role) {
      console.log('🎭 Role from internalProfile:', internalProfile.role);
      return internalProfile.role;
    }
    if (supabaseProfile?.role) {
      console.log('🎭 Role from supabaseProfile:', supabaseProfile.role);
      return supabaseProfile.role;
    }
    console.log('🎭 Using default role: player');
    return 'player';
  }, [internalUser?.role, internalProfile?.role, supabaseProfile?.role]);
  
  // Format role for display (e.g., "team-owner" -> "Team Owner")
  const role = formatRoleForDisplay(roleRaw);
  
  console.log('🎭 UserMenuData - Role determination:', {
    internalUserRole: internalUser?.role,
    internalProfileRole: internalProfile?.role,
    supabaseProfileRole: supabaseProfile?.role,
    finalRoleRaw: roleRaw,
    finalRoleFormatted: role
  });
  
  // Memoize the display name calculation - prioritize @username format
  const displayName = useMemo(() => {
    let username = '';
    
    // For internal users, check both profile and user data first
    if (internalUser?.username) {
      username = internalUser.username;
    } else if (internalProfile?.username) {
      username = internalProfile.username;
    }
    // For Supabase users, prioritize username
    else if (supabaseUser && supabaseProfile?.username) {
      username = supabaseProfile.username;
    }
    // Fallback to email username for Supabase users only if we have a stable email
    else if (supabaseUser?.email) {
      username = supabaseUser.email.split('@')[0];
    }
    
    // Return @username format if we have a username, otherwise fallback
    return username ? `@${username}` : 'User';
  }, [internalUser?.username, internalProfile?.username, supabaseProfile?.username, supabaseUser?.email, supabaseUser]);

  // Calculate initials once and keep them stable
  const initials = useMemo(() => {
    // Skip the @ symbol and use the first character of the username
    return displayName.startsWith('@') ? displayName.charAt(1).toUpperCase() : displayName.charAt(0).toUpperCase();
  }, [displayName]);

  // Get user slug for profile routes
  const userSlug = useMemo(() => {
    if (isInternalUser && internalUser) {
      return internalUser.username;
    }
    return supabaseProfile?.slug || supabaseProfile?.username;
  }, [isInternalUser, internalUser, supabaseProfile?.slug, supabaseProfile?.username]);

  // Profile route determination
  const profileRoute = useMemo(() => {
    if (userSlug) {
      return `/${userSlug}`;
    }
    return isInternalUser ? '/internal-profile' : '/profile';
  }, [userSlug, isInternalUser]);

  // Load profile photo with stable state management
  useEffect(() => {
    const loadProfilePhoto = async () => {
      let photoPath: string | null = null;
      
      console.log('🖼️ UserMenuData - Loading profile photo');
      console.log('  isInternalUser:', isInternalUser);
      console.log('  internalProfile?.profile_photo_url:', internalProfile?.profile_photo_url);
      console.log('  supabaseProfile?.profile_photo_url:', supabaseProfile?.profile_photo_url);
      
      if (isInternalUser && internalProfile?.profile_photo_url) {
        photoPath = internalProfile.profile_photo_url;
      } else if (supabaseProfile?.profile_photo_url) {
        photoPath = supabaseProfile.profile_photo_url;
      }
      
      console.log('  Resolved photoPath:', photoPath);
      console.log('  Current profilePhotoPath:', profilePhotoPath);
      
      // Always reload if we have a path - don't compare with current state since
      // the path string might be the same but we need a fresh signed URL
      if (photoPath) {
        // For internal users, we need to generate signed URLs if the path is not already a full URL
        if (isInternalUser && !photoPath.startsWith('http')) {
          try {
            const { photoUploadService } = await import('@/services/photoUploadService');
            const signedUrl = await photoUploadService.getProfilePhotoUrl(photoPath);
            console.log('  Generated signed URL:', signedUrl?.substring(0, 50) + '...');
            setProfilePhotoPath(signedUrl);
          } catch (error) {
            console.error('Error loading profile photo URL:', error);
            setProfilePhotoPath(photoPath);
          }
        } else {
          setProfilePhotoPath(photoPath);
        }
      } else if (profilePhotoPath !== null) {
        // Clear photo if there's no path but we had one before
        setProfilePhotoPath(null);
      }
    };

    loadProfilePhoto();
  }, [isInternalUser, internalProfile?.profile_photo_url, supabaseProfile?.profile_photo_url]);

  // Unified sign out function
  const signOut = isInternalUser ? internalLogout : supabaseSignOut;

  return {
    user,
    profile,
    displayName,
    initials,
    role,         // Now formatted for display (e.g., "Team Owner")
    roleRaw,      // Raw role value (e.g., "team-owner") for logic/comparisons
    profileRoute,
    profilePhotoPath,
    userSlug,
    signOut,
    internalLogout,
    isInternalUser
  };
};
